import { LockSimple, Plus, UsersThree } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Dialog as AriaDialog, Form } from "react-aria-components";
import { type Group, groupsQuery } from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { GroupDrawer } from "@/components/admin/GroupDrawer";
import { drawerSearch, QueryBoundary, useDrawerParam } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Button,
  type DataColumn,
  DataTable,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  InlineError,
  Modal,
  SearchField,
  TableSkeleton,
  TextField,
} from "@/components/ui";
import { api } from "@/lib/api";
import { focusFirstInvalid } from "@/lib/forms";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/admin/groups")({
  validateSearch: drawerSearch("group"),
  component: GroupsPage,
});

function GroupsPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_groups() }]);
  const q = useQuery(groupsQuery);
  const [openId, setOpenId] = useDrawerParam("group");
  const [creating, setCreating] = useState(false);

  return (
    <Page
      title={m.adm_nav_groups()}
      icon={UsersThree}
      meta={m.adm_groups_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <Button variant="primary" onPress={() => setCreating(true)}>
          <Plus size={14} aria-hidden="true" />
          {m.adm_groups_new()}
        </Button>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={4} columns={3} />} needs="users.manage">
        {(groups) => <GroupTable groups={groups} onOpen={setOpenId} onCreate={() => setCreating(true)} />}
      </QueryBoundary>
      <GroupDrawer groupId={openId} onClose={() => setOpenId(null)} />
      <NewGroupDialog isOpen={creating} onOpenChange={setCreating} onCreated={setOpenId} />
    </Page>
  );
}

function GroupTable({
  groups,
  onOpen,
  onCreate,
}: {
  groups: Group[];
  onOpen: (id: string) => void;
  onCreate: () => void;
}) {
  const [query, setQuery] = useState("");
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return groups.filter((g) => !needle || g.name.toLowerCase().includes(needle));
  }, [groups, query]);

  const columns: DataColumn<Group>[] = [
    {
      id: "name",
      header: m.field_name(),
      isRowHeader: true,
      className: "min-w-56",
      cell: (g) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-fg-muted">
            <UsersThree size={14} aria-hidden="true" />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate-title font-medium text-fg" title={g.name}>
              {g.name}
            </span>
            {g.description ? <span className="truncate-title text-xs text-fg-muted">{g.description}</span> : null}
          </div>
        </div>
      ),
    },
    {
      id: "members",
      header: m.adm_groups_col_members(),
      className: "w-32",
      cell: (g) => <span className="text-fg-muted tnum">{g.memberCount}</span>,
    },
    {
      id: "source",
      header: m.adm_groups_col_source(),
      className: "hidden w-44 sm:table-cell",
      cell: (g) =>
        g.managedByOidc ? (
          <Badge icon={LockSimple}>{m.adm_groups_sso()}</Badge>
        ) : (
          <span className="text-fg-muted">{m.adm_groups_local()}</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {groups.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <SearchField
            label={m.adm_groups_search()}
            placeholder={m.adm_groups_search()}
            value={query}
            onChange={setQuery}
            className="w-full sm:w-72"
          />
          <span className="ml-auto text-xs text-fg-muted tnum" aria-live="polite">
            {m.adm_users_count({ count: rows.length })}
          </span>
        </div>
      ) : null}
      <DataTable
        label={m.adm_nav_groups()}
        columns={columns}
        rows={rows}
        onRowAction={(g) => onOpen(g.id)}
        renderEmptyState={() =>
          groups.length === 0 ? (
            <EmptyState
              icon={UsersThree}
              title={m.adm_groups_empty_title()}
              description={m.adm_groups_empty_body()}
              action={
                <Button variant="secondary" onPress={onCreate}>
                  {m.adm_groups_new()}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={m.adm_groups_none_title()}
              description={m.adm_groups_none_body({ query })}
              action={
                <Button variant="secondary" onPress={() => setQuery("")}>
                  {m.filter_clear()}
                </Button>
              }
            />
          )
        }
      />
    </div>
  );
}

function NewGroupDialog({
  isOpen,
  onOpenChange,
  onCreated,
}: {
  isOpen: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
}) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <AriaDialog aria-labelledby="new-group-title" className="flex flex-col outline-none">
        {({ close }) => <NewGroupForm close={close} onCreated={onCreated} />}
      </AriaDialog>
    </Modal>
  );
}

function NewGroupForm({ close, onCreated }: { close: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<{ name?: string; form?: string }>({});
  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/api/v1/admin/groups", {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ["admin"] });
      toast.show({ title: m.adm_groups_created({ name: name.trim() }), tone: "success" });
      close();
      onCreated(g.id);
    },
    onError: (e) => setError({ form: adminError(e) }),
  });
  return (
    <Form
      validationBehavior="aria"
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) {
          setError({ name: m.validation_required() });
          focusFirstInvalid();
          return;
        }
        setError({});
        create.mutate();
      }}
    >
      <DialogHeader title={<span id="new-group-title">{m.adm_groups_new()}</span>} onClose={close} />
      <DialogBody className="flex flex-col gap-4 pb-2">
        <p className="text-pretty">{m.adm_groups_new_intro()}</p>
        {error.form ? <InlineError message={error.form} /> : null}
        <TextField
          label={m.field_name()}
          name="name"
          autoFocus
          autoComplete="off"
          placeholder={m.adm_groups_placeholder()}
          value={name}
          onChange={(v) => {
            setName(v);
            setError({});
          }}
          error={error.name}
        />
        <TextField
          label={m.field_description()}
          name="description"
          multiline
          rows={2}
          value={description}
          onChange={setDescription}
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onPress={close}>
          {m.common_cancel()}
        </Button>
        <Button type="submit" variant="primary" isPending={create.isPending}>
          {m.adm_groups_create()}
        </Button>
      </DialogFooter>
    </Form>
  );
}
